import React, {useCallback, useState} from 'react'
import styled, {useTheme} from 'styled-components'
import Tooltip from '@material-ui/core/Tooltip'
import FileCopyIcon from '@material-ui/icons/FileCopy'
import AssignmentIcon from '@material-ui/icons/Assignment'
import IconButton from 'lib/ui/IconButton'
import {useIfMounted} from 'lib/dom'
import {Font} from 'lib/FontSelect'

// Width units are incompatible across surfaces (grid columns, percent,
// pixels), so each unit gets its own key — a pasted width only applies to
// surfaces using the same unit.
export interface ButtonStyleClipboard {
  font?: Font | null
  fontSize?: number
  textColor?: string
  hoverTextColor?: string
  backgroundColor?: string
  hoverBackgroundColor?: string
  disableHover?: boolean
  borderColor?: string
  hoverBorderColor?: string
  borderWidth?: number
  borderRadius?: number
  height?: number
  widthGrid?: number
  widthPercent?: number
  widthPixels?: number
}

const STORAGE_KEY = 'obvio:button-style-clipboard'

function readButtonStyleClipboard(): ButtonStyleClipboard | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

const CONFIRMATION_RESET_DELAY_MS = 1500

export default function ButtonStyleClipboardActions(props: {
  values: ButtonStyleClipboard
  onPaste: (style: ButtonStyleClipboard) => void
  className?: string
}) {
  const [copied, setCopied] = useState(false)
  const [pasted, setPasted] = useState(false)
  const ifMounted = useIfMounted()
  const theme = useTheme()

  const hasStyle = Boolean(readButtonStyleClipboard())

  const resetAfterDelay = useCallback(
    (setConfirming: (confirming: boolean) => void) => {
      setConfirming(true)
      setTimeout(
        ifMounted(() => setConfirming(false)),
        CONFIRMATION_RESET_DELAY_MS,
      )
    },
    [ifMounted],
  )

  const handleCopy = () => {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(props.values))
    resetAfterDelay(setCopied)
  }

  const handlePaste = () => {
    const style = readButtonStyleClipboard()
    if (!style) {
      return
    }

    props.onPaste(style)
    resetAfterDelay(setPasted)
  }

  return (
    <Container className={props.className}>
      <Label>Style Clipboard</Label>
      <Tooltip title={copied ? 'Copied!' : 'Copy styles'}>
        <IconWrap>
          <IconButton aria-label="copy styles" onClick={handleCopy}>
            <FileCopyIcon style={{color: theme.colors.primary}} />
          </IconButton>
        </IconWrap>
      </Tooltip>
      <Tooltip
        title={
          pasted ? 'Pasted!' : hasStyle ? 'Paste styles' : 'Copy a style first'
        }
      >
        <IconWrap>
          <IconButton
            aria-label="paste styles"
            disabled={!hasStyle}
            onClick={handlePaste}
          >
            <AssignmentIcon
              style={{
                color: hasStyle ? theme.colors.primary : theme.colors.disabled,
              }}
            />
          </IconButton>
        </IconWrap>
      </Tooltip>
    </Container>
  )
}

const Container = styled.div`
  position: sticky;
  top: -1px;
  z-index: 2;
  display: flex;
  align-items: center;
  gap: ${(props) => props.theme.spacing[2]};
  padding: ${(props) => props.theme.spacing[2]} 0;
  margin-bottom: ${(props) => props.theme.spacing[2]};
  background-color: ${(props) => props.theme.colors.panel.background};
`

const Label = styled.div`
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${(props) => props.theme.colors.text.muted};
  margin-right: auto;
`

const IconWrap = styled.span`
  display: inline-flex;
  align-items: center;
`
